import cookies, { initialState as initialCookiesState } from './cookies/Cookies.reducer';
import disclosures, {
    initialState as initialDisclosuresState,
} from './disclosures/Disclosures.reducer';
import { genericReducer, passthroughReducer } from '../../../common/helpers/ReduxHelpers';
import iab, { initialState as initialIABState } from './iab/IAB.reducer';
import palette, { initialState as initialPaletteState } from './palette/Palette.reducer';
import scripts, { initialState as initialScriptsState } from './scripts/Scripts.reducer';

import { types as ActionTypes } from './Configuration.actions';
import { types as ConfigActionTypes } from '../ConfigDetails.actions';
import { combineReducers } from 'redux';
import { createTranslationOverridesFromState } from '../../utils';
import { defaultConfiguration } from '../../constants';
import reduceReducers from 'reduce-reducers';
import set from 'lodash/set';

export const initialState = {
    additionalLinks: defaultConfiguration.additionalLinks,
    allowTimeout: defaultConfiguration.allowTimeout,
    analyticsOnByDefault: defaultConfiguration.analyticsOnByDefault,
    analyticsOnByDefaultJurisdictions: defaultConfiguration.analyticsOnByDefaultJurisdictions,
    ccpaRelaxed: defaultConfiguration.ccpaRelaxed,
    codeSplitting: defaultConfiguration.codeSplitting,
    cookies: initialCookiesState,
    crossDomain: defaultConfiguration.crossDomain,
    customFlavorMapping: defaultConfiguration.customFlavorMapping,
    deleteStorageOnOptout: defaultConfiguration.deleteStorageOnOptout,
    disclosures: initialDisclosuresState,
    dntSupport: defaultConfiguration.dntSupport,
    documentPatching: defaultConfiguration.documentPatching,
    enableDoNotSell: defaultConfiguration.enableDoNotSell,
    enableDoNotSellDefault: defaultConfiguration.enableDoNotSellDefault,
    doNotSellCategories: defaultConfiguration.doNotSellCategories,
    externalStylesheet: defaultConfiguration.externalStylesheet,
    forcedClassifyEnabled: defaultConfiguration.forcedClassifyEnabled,
    forceManagePreferences: defaultConfiguration.forceManagePreferences,
    googleConsent: defaultConfiguration.googleConsent,
    amazonConsent: defaultConfiguration.amazonConsent,
    gpcSupport: defaultConfiguration.gpcSupport,
    iab: initialIABState,
    iabEnabled: defaultConfiguration.iabEnabled,
    iframeBlocking: defaultConfiguration.iframeBlocking,
    iframes: defaultConfiguration.iframes,
    legacyBrowserSupport: defaultConfiguration.legacyBrowserSupport,
    localStorage: defaultConfiguration.localStorage,
    localStorageBlocking: defaultConfiguration.localStorageBlocking,
    managePreferencesEnabled: defaultConfiguration.managePreferencesEnabled,
    microsoftConsent: defaultConfiguration.microsoftConsent,
    palette: initialPaletteState,
    policyLinkInDrawer: defaultConfiguration.policyLinkInDrawer,
    policyLinkText: defaultConfiguration.policyLinkText,
    remoteConsent: defaultConfiguration.remoteConsent,
    scripts: initialScriptsState,
    showConsentUuid: defaultConfiguration.showConsentUuid,
    showOptOutWidget: defaultConfiguration.showOptOutWidget,
    showWidget: defaultConfiguration.showWidget,
    storagePolicyHref: defaultConfiguration.storagePolicyHref,
    tattleSampling: defaultConfiguration.tattleSampling,
    timeoutSeconds: defaultConfiguration.timeoutSeconds,
    translationOverrides: {},
    translations: defaultConfiguration.translations,
    variantMapping: defaultConfiguration.variantMapping,
};

const configKeys = Object.keys(initialState);

const extractConfigurationKeys = (config, defaultConfig) =>
    configKeys.reduce((newConfig, key) => {
        if (key === 'iab' || key === 'palette' || key === 'translationOverrides') {
            return newConfig;
        }

        newConfig[key] = config[key] === undefined ? defaultConfig[key] : config[key];
        if (key === 'translations') {
            // We need to build the CMP overrides for translations
            newConfig.translationOverrides = createTranslationOverridesFromState(newConfig[key]);
        }

        return newConfig;
    }, {});

// An empty mapping isn't stored, so an absent key means cleared, not unchanged.
const responseFallbacks = state => ({ ...state, variantMapping: {} });

export default reduceReducers(
    initialState,
    combineReducers({
        additionalLinks: passthroughReducer(initialState.additionalLinks),
        allowTimeout: passthroughReducer(initialState.allowTimeout),
        analyticsOnByDefault: passthroughReducer(initialState.analyticsOnByDefault),
        analyticsOnByDefaultJurisdictions: passthroughReducer(
            initialState.analyticsOnByDefaultJurisdictions
        ),
        ccpaRelaxed: passthroughReducer(initialState.ccpaRelaxed),
        codeSplitting: passthroughReducer(initialState.codeSplitting),
        cookies,
        crossDomain: passthroughReducer(initialState.crossDomain),
        customFlavorMapping: passthroughReducer(initialState.customFlavorMapping),
        deleteStorageOnOptout: passthroughReducer(initialState.deleteStorageOnOptout),
        disclosures,
        dntSupport: passthroughReducer(initialState.dntSupport),
        documentPatching: passthroughReducer(initialState.documentPatching),
        enableDoNotSell: passthroughReducer(initialState.enableDoNotSell),
        enableDoNotSellDefault: passthroughReducer(initialState.enableDoNotSellDefault),
        doNotSellCategories: passthroughReducer(initialState.doNotSellCategories),
        externalStylesheet: passthroughReducer(initialState.externalStylesheet),
        forcedClassifyEnabled: passthroughReducer(initialState.forcedClassifyEnabled),
        forceManagePreferences: passthroughReducer(initialState.forceManagePreferences),
        googleConsent: passthroughReducer(initialState.googleConsent),
        amazonConsent: passthroughReducer(initialState.amazonConsent),
        gpcSupport: passthroughReducer(initialState.gpcSupport),
        iab,
        iabEnabled: passthroughReducer(initialState.iabEnabled),
        iframeBlocking: passthroughReducer(initialState.iframeBlocking),
        iframes: passthroughReducer(initialState.iframes),
        legacyBrowserSupport: passthroughReducer(initialState.legacyBrowserSupport),
        localStorage: passthroughReducer(initialState.localStorage),
        localStorageBlocking: passthroughReducer(initialState.localStorageBlocking),
        managePreferencesEnabled: passthroughReducer(initialState.managePreferencesEnabled),
        microsoftConsent: passthroughReducer(initialState.microsoftConsent),
        palette,
        policyLinkInDrawer: passthroughReducer(initialState.policyLinkInDrawer),
        policyLinkText: passthroughReducer(initialState.policyLinkText),
        remoteConsent: passthroughReducer(initialState.remoteConsent),
        scripts,
        showConsentUuid: passthroughReducer(initialState.showConsentUuid),
        showOptOutWidget: passthroughReducer(initialState.showOptOutWidget),
        showWidget: passthroughReducer(initialState.showWidget),
        storagePolicyHref: passthroughReducer(initialState.storagePolicyHref),
        tattleSampling: passthroughReducer(initialState.tattleSampling),
        timeoutSeconds: passthroughReducer(initialState.timeoutSeconds),
        translationOverrides: passthroughReducer(initialState.translationOverrides),
        translations: passthroughReducer(initialState.translations),
        variantMapping: passthroughReducer(initialState.variantMapping),
    }),
    genericReducer(initialState, {
        [ConfigActionTypes.createSuccess]: (state, { response }) => {
            const { configuration = {} } = response;
            return {
                ...state,
                ...extractConfigurationKeys(configuration, responseFallbacks(state)),
            };
        },
        [ConfigActionTypes.fetchDetailsSuccess]: (state, { response }) => {
            const { configuration = {} } = response;
            return {
                ...state,
                ...extractConfigurationKeys(configuration, {
                    ...responseFallbacks(state),
                    // Defaults for pre-existing configs without these settings:
                    dntSupport: false,
                    forcedClassifyEnabled: false,
                    gpcSupport: false,
                    legacyBrowserSupport: false,
                    forceManagePreferences: false,
                    tattleSampling: 1,
                }),
            };
        },
        [ConfigActionTypes.updateSuccess]: (state, { response }) => {
            const { configuration = {} } = response;
            return {
                ...state,
                ...extractConfigurationKeys(configuration, responseFallbacks(state)),
            };
        },
        [ActionTypes.fieldChange]: (state, { field, value }) => {
            if (
                field === 'scripts' ||
                field === 'cookies' ||
                field === 'iframes' ||
                field === 'localStorage' ||
                field === 'palette' ||
                field === 'iab' ||
                field === 'disclosures'
            ) {
                return state;
            }
            const newState = { ...state };
            const fieldPath = field.split('.');
            set(newState, field.split('.'), value);
            switch (fieldPath[0]) {
                case 'translations': {
                    newState.translationOverrides = createTranslationOverridesFromState(
                        newState.translations
                    );
                }
            }
            return newState;
        },
    })
);
